import React from 'react';
import { Link } from 'react-router-dom';
import { Cloud, Server, Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';

const iconMap = {
  cloud: Cloud,
  server: Server,
  mail: Mail
};

export const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Our Services</h1>
          <p className="text-xl text-gray-600">
            Comprehensive IT solutions designed to help your business succeed in the digital age
          </p>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-20">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div key={service.id} className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-dense' : ''}`}>
                  <div className={isEven ? '' : 'lg:col-start-2'}>
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6">
                      <IconComponent className="text-blue-600" size={32} />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">{service.title}</h2>
                    <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Link to="/contact">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                        Get Started
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  </div>
                  <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}>
                    <Card className="shadow-xl hover:shadow-2xl transition-shadow duration-300">
                      <CardContent className="p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">What's Included</h3>
                        <div className="space-y-4">
                          <div className="border-l-4 border-blue-600 pl-4">
                            <h4 className="font-semibold text-gray-900 mb-1">Initial Assessment</h4>
                            <p className="text-sm text-gray-600">Comprehensive evaluation of your current setup and requirements</p>
                          </div>
                          <div className="border-l-4 border-blue-600 pl-4">
                            <h4 className="font-semibold text-gray-900 mb-1">Custom Implementation</h4>
                            <p className="text-sm text-gray-600">Tailored deployment based on your specific business needs</p>
                          </div>
                          <div className="border-l-4 border-blue-600 pl-4">
                            <h4 className="font-semibold text-gray-900 mb-1">Training & Support</h4>
                            <p className="text-sm text-gray-600">Hands-on training for your team and ongoing technical support</p>
                          </div>
                          <div className="border-l-4 border-blue-600 pl-4">
                            <h4 className="font-semibold text-gray-900 mb-1">Ongoing Optimization</h4>
                            <p className="text-sm text-gray-600">Regular health checks and performance improvements</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Additional Services</h2>
            <p className="text-xl text-gray-600">We offer more to support your IT needs</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Cloud Migration', description: 'Seamless transition to cloud platforms with zero downtime' },
              { title: 'IT Security', description: 'Comprehensive security audits and implementation' },
              { title: 'Data Backup', description: 'Automated backup solutions for business continuity' },
              { title: 'IT Training', description: 'Custom training programs for your team' }
            ].map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Not Sure Which Service You Need?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Book a free consultation and we'll help you find the perfect solution for your business.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl hover:shadow-2xl transition-all duration-200">
              Book Free Consultation
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
